import type { ReactNode } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { formatDate, formatGbp, planLabel, EMPTY } from '@/lib/customer-format'
import type { CustomerAccount, CustomerBilling } from '@/lib/customer-types'
import { CopyableId } from './CopyableId'

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="font-label text-[0.65rem] uppercase tracking-wider text-cmd-outline">
        {label}
      </dt>
      <dd className="break-words text-sm text-white/90">{children ?? EMPTY}</dd>
    </div>
  )
}

/** Tone for a billing / payment status string. */
function statusVariant(status: string | null): 'success' | 'destructive' | 'secondary' {
  const s = (status ?? '').toLowerCase()
  if (/(paid|active|succeeded|ok|current)/.test(s)) return 'success'
  if (/(fail|overdue|past_due|declined|error|cancel)/.test(s)) return 'destructive'
  return 'secondary'
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-cmd-outline">{EMPTY}</span>
  return <Badge variant={statusVariant(status)}>{status}</Badge>
}

export function CustomerOverview({
  account,
  billing,
}: {
  account: CustomerAccount
  billing: CustomerBilling
}) {
  const { lastInvoice, paymentMethod } = billing

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Account facts */}
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Fact label="Plan">{planLabel(account.plan)}</Fact>
            <Fact label="Signed up">{formatDate(account.signupDate)}</Fact>
            <Fact label="Country">{account.countryCode ?? EMPTY}</Fact>
            <Fact label="Companies House no.">{account.registrationNumber ?? EMPTY}</Fact>
            <Fact label="ICO no.">{account.icoNumber ?? EMPTY}</Fact>
            <div className="col-span-2">
              <Fact label="Registered address">{account.address ?? EMPTY}</Fact>
            </div>
            <div className="col-span-2">
              <Fact label="Stripe customer">
                <CopyableId value={account.stripeCustomerId} />
              </Fact>
            </div>
            <div className="col-span-2">
              <Fact label="Stripe subscription">
                <CopyableId value={account.stripeSubscriptionId} />
              </Fact>
            </div>
            {account.founderRescueEmailSentAt && (
              <div className="col-span-2">
                <p className="text-xs text-cmd-outline">
                  Onboarding rescue email sent {formatDate(account.founderRescueEmailSentAt)}
                </p>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Billing summary */}
      <Card>
        <CardHeader>
          <CardTitle>Billing</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Fact label="MRR">
              <span className="font-mono tabular-nums">{formatGbp(billing.mrr)}</span>
            </Fact>
            <Fact label="Payment status">
              <StatusBadge status={billing.lastPaymentStatus} />
            </Fact>
            <Fact label="Next invoice">{formatDate(billing.nextInvoiceDate)}</Fact>
            <Fact label="Next invoice amount">
              <span className="font-mono tabular-nums">{formatGbp(billing.nextInvoiceAmount)}</span>
            </Fact>
            <Fact label="Last invoice">
              {lastInvoice ? (
                <span className="font-mono tabular-nums">
                  {formatGbp(lastInvoice.totalGbp)}
                </span>
              ) : (
                EMPTY
              )}
            </Fact>
            <Fact label="Last invoice status">
              <StatusBadge status={lastInvoice?.status ?? null} />
            </Fact>
            <div className="col-span-2">
              <Fact label="Payment method">
                {paymentMethod ? (
                  <span>
                    {paymentMethod.provider}
                    {paymentMethod.bankAccountLast4 ? ` ····${paymentMethod.bankAccountLast4}` : ''}
                    {' · '}
                    <span className="text-cmd-outline">{paymentMethod.status}</span>
                  </span>
                ) : (
                  EMPTY
                )}
              </Fact>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  )
}
